import React, { useState, useMemo, useEffect, useRef } from "react";
import tstyles from "../styles/TeacherResources.module.scss";
import nstyles from "../styles/noticeboard.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { profileDetails } from "../../../../Redux/profile/profile.action";
import {
  deleteNotice,
  noticeList,
} from "../../../../Redux/Teacher_Panel/notice_board/notice.action";
import { Loader } from "lucide-react";
import ToastMessage from "../../../../utils/toasts/toastMessage";
import NoticeBoardPage1 from "../notice_boardPage1/NoticeBoardPage1";
import { BASE_IMAGE_URL } from "../../../../utils/api";
import { createPortal } from "react-dom";

const CARDS_PER_PAGE = 4;

function NoticeCard({ notice, onEdit, onDeleteRequest }) {

  const { className, division, subject, title, desc, date, image, eventDate } =
    notice;
  const [showCardMenu, setShowCardMenu] = useState(false);
  const cardMenuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (cardMenuRef.current && !cardMenuRef.current.contains(e.target)) {
        setShowCardMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <article className={nstyles.noticeCard} role="article" aria-label={title}>
      <div className={nstyles.cardHeader} style={{ position: "relative" }}>
        <div>
          <p
            className={nstyles.cardMeta}
          >{`${className}${division} - ${subject}`}</p>
          <h3 className={nstyles.cardTitle}>Title: {title}</h3>
          {/* {meta && <p className={nstyles.cardMeta}>{meta}</p>} */}
          <span className={nstyles.cardDate}>Date: {eventDate}</span>
        </div>

        <div ref={cardMenuRef} className={nstyles.cardMenuWrap}>
          <button
            type="button"
            onClick={() => setShowCardMenu((s) => !s)}
            aria-label="notice options"
            aria-haspopup="menu"
            aria-expanded={showCardMenu}
            className={nstyles.menuTrigger}
          >
            ⋮
          </button>

          {showCardMenu && (
            <div role="menu" className={nstyles.menuDropdown}>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setShowCardMenu(false);
                  onEdit(notice);
                }}
                className={nstyles.menuItem}
              >
                Edit
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setShowCardMenu(false);
                  onDeleteRequest(notice.id);
                }}
                className={nstyles.menuItemDanger}
              >
                Delete
              </button>
            </div>
          )}
        </div>

        {image ? (
          <div
            aria-hidden="true"
            style={{
              width: 66,
              height: 66,
              flex: "0 0 66px",
              borderRadius: 10,
              overflow: "hidden",
              background: "#f1f1f1",
              padding: 0,
              cursor: "pointer",
              marginRight: "30px",
            }}
            onClick={() => {
              const imageUrl = `${BASE_IMAGE_URL}/data/uploads/announcements/${image}`;
              window.open(imageUrl, "_blank", "noopener,noreferrer");
            }}
          >
            <img
              src={`${BASE_IMAGE_URL}/data/uploads/announcements/${image}`}
              alt=""
              loading="lazy"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          </div>
        ) : (
          // <div className={nstyles.badge} aria-hidden="true" />
          <span></span>
        )}
      </div>
      <p className={nstyles.cardDesc}>{desc}</p>

      <div className={nstyles.cardFooter}>
        <span className={nstyles.cardDate}>Published: {date}</span>
      </div>
    </article>
  );
}

function getPageRange(current, total, siblingCount = 1) {
  const totalNumbers = siblingCount * 2 + 5;
  if (total <= totalNumbers)
    return Array.from({ length: total }, (_, i) => i + 1);
  const leftSibling = Math.max(current - siblingCount, 1);
  const rightSibling = Math.min(current + siblingCount, total);
  const showLeftEllipsis = leftSibling > 2;
  const showRightEllipsis = rightSibling < total - 1;
  if (!showLeftEllipsis && showRightEllipsis) {
    const leftRange = Array.from(
      { length: 3 + siblingCount * 2 },
      (_, i) => i + 1,
    );
    return [...leftRange, "...", total];
  }
  if (showLeftEllipsis && !showRightEllipsis) {
    const rightRange = Array.from(
      { length: 3 + siblingCount * 2 },
      (_, i) => total - (3 + siblingCount * 2) + i + 1,
    );
    return [1, "...", ...rightRange];
  }
  const middleRange = Array.from(
    { length: rightSibling - leftSibling + 1 },
    (_, i) => leftSibling + i,
  );
  return [1, "...", ...middleRange, "...", total];
}

export default function NoticeBoardPage() {
  const dispatch = useDispatch();
  const [q, setQ] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingNotice, setEditingNotice] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  // single source of truth for delete flow — this is what was missing before
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [toastState, setToastState] = useState({
    show: false,
    message: "",
    type: "",
  });

  const NoticeListStore = useSelector(
    (state) => state?.NoticeTeacherManager?.notice?.Noticeboard,
  );

  useEffect(() => {
    dispatch(profileDetails());
    dispatch(noticeList());
  }, [dispatch]);

  const showToast = (message, type = "success") =>
    setToastState({ show: true, message, type });

  const notices = useMemo(() => {
    const raw = NoticeListStore ? [...NoticeListStore].reverse() : [];
    return raw.map((n) => ({
      id: n.id,
      subject: n.subject,
      title: n.title,
      desc: n.description,
      className: n.class_name,
      division: n.division,
      eventDate: n.event_date
        ? new Date(n.event_date).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })
        : "",
      date: n.created_at
        ? new Date(n.created_at).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })
        : "",
      image: n.image,
    }));
  }, [NoticeListStore]);

  const filteredNotices = useMemo(() => {
    if (!q.trim()) return notices;
    const query = q.trim().toLowerCase();
    return notices.filter((n) =>
      [n.title, n.subject]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(query),
    );
  }, [q, notices]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredNotices.length / CARDS_PER_PAGE),
  );
  const safePage = Math.min(currentPage, totalPages);

  const paginatedNotices = useMemo(() => {
    const start = (safePage - 1) * CARDS_PER_PAGE;
    return filteredNotices.slice(start, start + CARDS_PER_PAGE);
  }, [filteredNotices, safePage]);

  const pageRange = useMemo(
    () => getPageRange(safePage, totalPages, 1),
    [safePage, totalPages],
  );

  const rangeStart =
    filteredNotices.length === 0 ? 0 : (safePage - 1) * CARDS_PER_PAGE + 1;
  const rangeEnd = Math.min(safePage * CARDS_PER_PAGE, filteredNotices.length);

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const handleSearch = (e) => {
    e?.preventDefault?.();
    setCurrentPage(1);
  };

  // ---- create / edit ----
  const handleAdd = () => setShowForm(true);
  const handleCreateCancel = () => setShowForm(false);
  const handleCreateSuccess = (message) => {
    setShowForm(false);
    setCurrentPage(1);
    showToast(message || "Notice published successfully.");
  };

  const handleEditNotice = (notice) => setEditingNotice(notice);
  const handleEditCancel = () => setEditingNotice(null);
  const handleEditSuccess = (message) => {
    setEditingNotice(null);
    showToast(message || "Notice updated successfully.");
  };

  // ---- delete ----
  const handleDeleteRequest = (id) => setDeleteTargetId(id);
  const handleCancelDelete = () => setDeleteTargetId(null);

  const handleConfirmDelete = async () => {
    if (!deleteTargetId) return;
    setDeleting(true);
    try {
      const result = await dispatch(deleteNotice(deleteTargetId));
      if (result?.success) {
        await dispatch(noticeList());
        showToast("Notice deleted successfully.", "success");
      } else {
        showToast(result?.message || "Could not delete notice.", "error");
      }
    } catch {
      showToast("Something went wrong.", "error");
    } finally {
      setDeleting(false);
      setDeleteTargetId(null);
    }
  };

  if (showForm) {
    return (
      <NoticeBoardPage1
        onCancel={handleCreateCancel}
        onSuccess={handleCreateSuccess}
      />
    );
  }

  if (editingNotice) {
    return (
      <NoticeBoardPage1
        initialNotice={editingNotice}
        onCancel={handleEditCancel}
        onSuccess={handleEditSuccess}
      />
    );
  }

  return (
    <div className={tstyles.container}>
      <main className={tstyles.main}>
        <div className={nstyles.noticeRect} aria-label="Notice Board Panel">
          <div className={nstyles.noticeHeaderRow}>
            <div className={nstyles.headerLeftGroup}>
              <h2 className={nstyles.pageTitle}>Notice Board</h2>
            </div>

            <div className={nstyles.searchAdd}>
              <form
                className={nstyles.searchSpan}
                onSubmit={handleSearch}
                role="search"
                aria-label="Search notices"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className={nstyles.searchIcon}
                  aria-hidden="true"
                >
                  <path
                    d="M21 21l-4.35-4.35"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <circle
                    cx="11"
                    cy="11"
                    r="6"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                </svg>
                <input
                  type="search"
                  className={nstyles.searchInput}
                  placeholder="Search by title or subject"
                  aria-label="Search Notices"
                  name="q"
                  value={q}
                  onChange={(e) => {
                    setQ(e.target.value);
                    setCurrentPage(1);
                  }}
                  autoComplete="off"
                />
              </form>

              <button
                className={nstyles.addBtn}
                onClick={handleAdd}
                aria-label="Add new notice"
              >
                + Create
              </button>
            </div>
          </div>

          <div className={nstyles.cardsRow}>
            {paginatedNotices.length === 0 ? (
              <p className={nstyles.emptyState}>No notices found.</p>
            ) : (
              paginatedNotices.map((n) => (
                <NoticeCard
                  key={n.id}
                  notice={n}
                  onEdit={handleEditNotice}
                  onDeleteRequest={handleDeleteRequest}
                />
              ))
            )}
          </div>

          {filteredNotices.length > 0 && (
            <div className={nstyles.paginationBar}>
              {totalPages > 1 && (
                <span className={nstyles.paginationSummary}>
                  Showing {rangeStart}–{rangeEnd} of {filteredNotices.length}{" "}
                  notices
                </span>
              )}
              {totalPages > 1 && (
                <nav
                  className={nstyles.pagination}
                  aria-label="Notice pagination"
                >
                  <button
                    className={nstyles.pageBtn}
                    onClick={() => goToPage(1)}
                    disabled={safePage === 1}
                    aria-label="First page"
                    title="First page"
                  >
                    «
                  </button>
                  <button
                    className={nstyles.pageBtn}
                    onClick={() => goToPage(safePage - 1)}
                    disabled={safePage === 1}
                    aria-label="Previous page"
                  >
                    Prev
                  </button>
                  {pageRange.map((page, idx) =>
                    page === "..." ? (
                      <span
                        key={`ellipsis-${idx}`}
                        className={nstyles.pageEllipsis}
                      >
                        …
                      </span>
                    ) : (
                      <button
                        key={page}
                        className={`${nstyles.pageBtn} ${page === safePage ? nstyles.pageBtnActive : ""}`}
                        onClick={() => goToPage(page)}
                        aria-current={page === safePage ? "page" : undefined}
                      >
                        {page}
                      </button>
                    ),
                  )}
                  <button
                    className={nstyles.pageBtn}
                    onClick={() => goToPage(safePage + 1)}
                    disabled={safePage === totalPages}
                    aria-label="Next page"
                  >
                    Next
                  </button>
                  <button
                    className={nstyles.pageBtn}
                    onClick={() => goToPage(totalPages)}
                    disabled={safePage === totalPages}
                    aria-label="Last page"
                    title="Last page"
                  >
                    »
                  </button>
                </nav>
              )}
            </div>
          )}
        </div>
      </main>

      {deleteTargetId !== null &&
        createPortal(
          <div
            className="modal fade show"
            tabIndex={-1}
            aria-hidden="true"
            style={{
              display: "block",
              background: "rgba(0,0,0,0.5)",
              position: "fixed",
              inset: 0,
              zIndex: 1050,
            }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="border-0 bdrs40 modal-content p-4 text-center">
                <button
                  type="button"
                  className="btn-close position-absolute end-0 top-0 m-3"
                  onClick={handleCancelDelete}
                  disabled={deleting}
                />
                <h4 className="fw600 mb-3">Delete</h4>
                <p className="mb-4">
                  Are you sure you want to delete the notice?
                </p>
                <div className="d-flex justify-content-center gap-3">
                  <button
                    className="btn px-4"
                    style={{
                      backgroundColor: "#000",
                      color: "#fff",
                      border: "1px solid #000",
                    }}
                    onClick={handleCancelDelete}
                    disabled={deleting}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn px-4"
                    style={{
                      backgroundColor: "#dc3545",
                      color: "#fff",
                      border: "1px solid #dc3545",
                    }}
                    onClick={handleConfirmDelete}
                    disabled={deleting}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}

      <Loader show={deleting} />
      <ToastMessage
        show={toastState.show}
        onClose={() => setToastState({ show: false, message: "", type: "" })}
        message={toastState.message}
        type={toastState.type}
      />
    </div>
  );
}
